import type { ISmartData } from '$lib/models/ISmartData';

/** A point cloud's overall status, derived from its upload status, tilesets and jobs. */
export type PointCloudStatus =
	| 'NotSet'
	| 'Uploading'
	| 'UploadComplete'
	| 'Processing'
	| 'Complete'
	| 'Failed';

/**
 * A tiling or Pointly job is queued or running. The point cloud can then be neither processed
 * again nor deleted. A metadata extraction does not count.
 */
export const hasActiveOrder = (item: ISmartData.PointCloudItem): boolean =>
	item.latestJobs.some(
		(job) =>
			job.type !== 'MetadataExtraction' && (job.status === 'Queued' || job.status === 'Processing')
	);

/** The tileset to show in the map: Pointly's classified one when it exists, otherwise the source. */
export const preferredTilesetTarget = (
	availableTilesets: ISmartData.PointCloudTarget[]
): ISmartData.PointCloudTarget | null =>
	availableTilesets.includes('Pointly')
		? 'Pointly'
		: availableTilesets.includes('Source')
			? 'Source'
			: null;

/**
 * `Complete` as soon as a tileset can be streamed, also while another one is being built.
 * `Processing` while a tiling or Pointly job is queued or running. `Failed` when the upload or
 * any latest job failed. `UploadComplete` means ready for processing. `NotSet` for a locked
 * point cloud, which has no status.
 */
export function pointCloudStatus(item: ISmartData.PointCloudItem): PointCloudStatus {
	if (item.status === null) return 'NotSet';
	if (item.status === 'UploadFailed') return 'Failed';
	if (item.status !== 'UploadComplete') return item.status;
	if (item.availableTilesets.length > 0) return 'Complete';
	if (hasActiveOrder(item)) return 'Processing';
	if (item.latestJobs.some((job) => job.status === 'Failed')) return 'Failed';
	return 'UploadComplete';
}

/** What failed, for a point cloud with status `Failed`. `null` for any other status. */
export function pointCloudFailureText(item: ISmartData.PointCloudItem): string | null {
	if (pointCloudStatus(item) !== 'Failed') return null;
	if (item.status === 'UploadFailed') return 'Opplastingen feilet';
	const failed = [...item.latestJobs]
		.filter((job) => job.status === 'Failed')
		.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0];
	return failed ? `${pointCloudJobTypeLabel[failed.type]} feilet` : null;
}

export const pointCloudStatusConfig: Record<
	PointCloudStatus,
	{ label: string; classes: string }
> = {
	NotSet: {
		label: 'Klar for opplasting',
		classes: 'bg-gray-200/50 border-gray-300 text-secondary'
	},
	Uploading: {
		label: 'Laster opp...',
		classes: 'bg-warning/10 border-warning text-secondary'
	},
	UploadComplete: {
		label: 'Klar til prosessering',
		classes: 'bg-warning/10 border-warning text-secondary'
	},
	Processing: {
		label: 'Klargjør for visning i kart...',
		classes: 'bg-info/10 border-info text-info'
	},
	Complete: {
		label: 'Klar til visning',
		classes: 'bg-success/10 border-success text-success'
	},
	Failed: {
		// The upload or any job; `pointCloudFailureText` says which.
		label: 'Feilet',
		classes: 'bg-error/10 border-error text-error'
	}
};

export const pointCloudJobTypeLabel: Record<ISmartData.PointCloudJobType, string> = {
	MetadataExtraction: 'Filkontroll',
	Tileset: '3D-tiles',
	Pointly: 'Berikelse'
};

export const pointCloudTargetLabel: Record<ISmartData.PointCloudTarget, string> = {
	Source: 'Original',
	Pointly: 'Beriket'
};

export const pointCloudJobStatusConfig: Record<
	ISmartData.PointCloudJobStatus,
	{ label: string; classes: string }
> = {
	Queued: { label: 'I kø', classes: 'bg-gray-200/50 text-secondary' },
	Processing: { label: 'Pågår', classes: 'bg-info/10 text-info' },
	Complete: { label: 'Ferdig', classes: 'bg-success/10 text-success' },
	Failed: { label: 'Feilet', classes: 'bg-error/10 text-error' }
};

export const imageStatusConfig: Record<
	ISmartData.ImageItem['status'],
	{ label: string; classes: string }
> = {
	NotSet: {
		label: 'Status ikke satt',
		classes: 'bg-gray-200/50 border-gray-300 text-secondary'
	},
	Uploading: {
		label: 'Laster opp...',
		classes: 'bg-warning/10 border-warning text-secondary'
	},
	NotConnectedToInfrastructure: {
		label: 'Not connected',
		classes: 'bg-warning/10 border-warning text-secondary'
	},
	Processing: {
		label: 'Behandler bilde...',
		classes: 'bg-white border-info text-info'
	},
	Complete: {
		label: 'Ferdig prosessert',
		classes: 'bg-success/10 border-success text-success'
	},
	Failed: {
		label: 'Feilet under prosessering',
		classes: 'bg-error/10 border-error text-error'
	}
};

export function getImageStatusLabel(status: ISmartData.ImageItem['status']): string {
	return imageStatusConfig[status]?.label || imageStatusConfig.NotSet.label;
}
